import { useEffect, useMemo, useState } from 'react'

import {
  Button,
  Collapsible,
  Drawer,
  SecondaryButton,
  Text,
  useNotifications,
} from '@joor/design-system'
import classNames from 'classnames'
import { useFormik } from 'formik'
import { useSelector } from 'react-redux'

import {
  OrderReviewGigaQueryDocument,
  OrderReviewGigaQueryQuery,
  VariantFilterValuesInput,
} from '__generated__/atlas-types'

import { download } from 'utils'
import {
  FORMAT_EXCEL,
  FORMAT_EXPORT_ORDER_OR_LINESHEET,
  FORMAT_INVOICE_PDF,
  FORMAT_PDF,
  downloadFileFromResponse,
  exportOrders,
} from 'utils/requests'
import { useUserAnalytics } from 'utils/sdks/userAnalytics'

import { useRenderToasters } from 'hooks/useRenderToasters'
import useWatchQuery from 'hooks/useWatchQuery'
import { getConnectedProvider } from 'payments/payments.transformations'
import { isProBrand } from 'store/currentUser/selectors'

import { useJoorPay } from '../../review.hooks'
import { ReviewPageIds } from '../../review.ids'
import {
  useAccountPaymentProvidersQuery,
  useInvoiceHasBeenSent,
} from '../../review.queries'
import {
  checkLiteRetailerConditions,
  checkProBrandConditions,
} from '../PayView/utils'
import { useTriggerOrderPdfExportTasks } from './downloadDrawer.mutations'
import { useListOrderExportTasks } from './downloadDrawer.queries'
import {
  AdvancedOptions,
  Footer,
  Form,
  StyledCheckbox,
  StyledRadioButton,
  Types,
} from './downloadDrawer.ui'
import { useOrderId } from 'features/Orders/orders.hooks'

const ADVANCED_OPTIONS_BY_TYPE = {
  [FORMAT_PDF]: [
    { label: 'Merge orders into one PDF file', value: 'merge_all_pdf' },
    {
      label: 'Exclude "Terms & Conditions" from PDFs',
      value: 'exclude_terms',
    },
    { label: 'Exclude images from PDFs', value: 'no_images_export' },
    { label: 'Linesheet format', value: 'linesheet_format' },
  ],
  [FORMAT_EXCEL]: [
    { label: 'Merge orders into one Excel file', value: 'merge_all_excel_po' },
    {
      label: 'Exclude Terms & Conditions" from Excel',
      value: 'exclude_terms_excel_po',
    },
    { label: 'Exclude images from Excel', value: 'no_images_export_excel_po' },
  ],
  [FORMAT_EXPORT_ORDER_OR_LINESHEET]: [],
  [FORMAT_INVOICE_PDF]: [],
}

const ADVANCED_OPTIONS_LABELS: Record<string, Record<string, string>> = {
  [FORMAT_PDF]: {
    merge_all_pdf: 'PDF Merge',
    exclude_terms: 'PDF Exclude Terms',
    no_images_export: 'PDF Exclude Images',
    linesheet_format: 'PDF Linesheet Format',
  },
  [FORMAT_EXCEL]: {
    merge_all_excel_po: 'Excel Merge',
    exclude_terms_excel_po: 'Excel Exclude Terms',
    no_images_export_excel_po: 'Excel Exclude Images',
  },
  [FORMAT_EXPORT_ORDER_OR_LINESHEET]: {},
  [FORMAT_INVOICE_PDF]: {},
}

type Props = {
  onClose: () => void
  testId?: string
  isLinesheetVisible?: boolean
  decodedWebOrderId?: string
  brandId?: string
}

type FormOptions = {
  format:
    | typeof FORMAT_PDF
    | typeof FORMAT_EXCEL
    | typeof FORMAT_EXPORT_ORDER_OR_LINESHEET
    | typeof FORMAT_INVOICE_PDF
  options: Record<string, boolean>
}

const toastMessages = {
  [FORMAT_PDF]: {
    success: {
      description: 'Order confirmation has been successfully downloaded.',
    },
    error: {
      title: 'Download error',
      description:
        'There was an error while downloading the order confirmation, please try again later.',
    },
  },
  [FORMAT_EXCEL]: {
    success: {
      description: 'Order confirmation has been successfully downloaded.',
    },
    error: {
      title: 'Download error',
      description:
        'There was an error while downloading the order confirmation, please try again later.',
    },
  },
  [FORMAT_EXPORT_ORDER_OR_LINESHEET]: {
    success: {
      description: 'Order has been successfully downloaded',
    },
    error: {
      title: 'Unable to export Order',
      description: 'An unexpected server error has occurred.',
    },
  },
  [FORMAT_INVOICE_PDF]: {
    success: {
      description: 'Order invoice has been successfully downloaded.',
    },
    error: {
      title: 'Unable to Export Order',
      description: 'An unexpected server error has occured.',
    },
  },
}

const getCheckedOptionsText = (checkedOptions: FormOptions): string[] => {
  let checked: string[] = []
  ADVANCED_OPTIONS_BY_TYPE[checkedOptions.format].forEach(({ value }) => {
    if (checkedOptions.options[value]) {
      checked.push(ADVANCED_OPTIONS_LABELS[checkedOptions.format][value])
    }
  })
  return checked
}

export const DownloadDrawer = ({
  onClose,
  isLinesheetVisible,
  decodedWebOrderId,
  brandId,
}: Props) => {
  const orderId = useOrderId()
  const { joorPayUseReach } = useJoorPay(brandId)
  const { hasInvoiceBeenSent } = useInvoiceHasBeenSent()
  const cachedOrderProducts = useWatchQuery<OrderReviewGigaQueryQuery>({
    query: OrderReviewGigaQueryDocument,
    variables: {
      filters: {
        ids: [orderId],
      },
      traitCodes: VariantFilterValuesInput.COLOR,
      limit: 20,
      offset: 0,
    },
  })

  const totalUnits =
    cachedOrderProducts?.orders.edges[0].node.products.totalUnits

  const { renderSuccessToast, renderErrorToast, renderWarningToast } =
    useRenderToasters()
  const { activeToasts, removeToasts } = useNotifications()
  const { triggerOrderPdfExportTasks } = useTriggerOrderPdfExportTasks()
  const [tasksIds, setTasksIds] = useState<string[]>([])
  const { data, startPolling, stopPolling } = useListOrderExportTasks(tasksIds)
  const { data: accountPaymentProviderData } =
    useAccountPaymentProvidersQuery(brandId)
  const isProBrandUser = useSelector(isProBrand)
  const loadingToast = useMemo(
    () => activeToasts.find(({ title }) => title === 'Download in progress'),
    [activeToasts],
  )
  useEffect(() => {
    if (data?.listOrderExportTasks?.exportTasks?.[0]?.downloadUrl) {
      download(data?.listOrderExportTasks?.exportTasks?.[0]?.downloadUrl)
      stopPolling()
      renderSuccessToast({
        title: 'Download successful',
        description: toastMessages[formik.values.format].success.description,
      })
    }
  }, [data?.listOrderExportTasks?.exportTasks])

  const formik = useFormik<FormOptions>({
    initialValues: {
      format: FORMAT_PDF,
      options: {},
    },
    onSubmit: async () => {
      renderWarningToast({
        title: 'Download in progress',
        description:
          'Please stay on this page until your download is complete.',
        shouldCloseManually: true,
      })
      try {
        if (formik.values.format === FORMAT_INVOICE_PDF) {
          const response = await triggerOrderPdfExportTasks(orderId)
          if (response.errors) {
            throw new Error()
          }
          if (response && response?.data?.triggerOrdersPdfExportTasks.taskIds) {
            setTasksIds(response.data.triggerOrdersPdfExportTasks.taskIds)
            startPolling(3000)
          }
        }
        if (exportOrdersToSizeSelected && decodedWebOrderId) {
          await downloadFileFromResponse(
            `/orders/download_excel/${decodedWebOrderId}/?include_linesheet=1`,
            {},
          )
          renderSuccessToast({
            title: 'Download successful',
            description:
              toastMessages[formik.values.format].success.description,
            shouldCloseManually: true,
          })
        }
        if (
          formik.values.format !== FORMAT_INVOICE_PDF &&
          !exportOrdersToSizeSelected
        ) {
          await exportOrders(
            [orderId],
            formik.values.format,
            formik.values.options,
          )
          renderSuccessToast({
            title: 'Download successful',
            description:
              toastMessages[formik.values.format].success.description,
          })
        }
      } catch (err) {
        renderErrorToast({
          title: toastMessages[formik.values.format].error.title,
          description: toastMessages[formik.values.format].error.description,
          shouldCloseManually: true,
        })
      }
    },
  })
  const { trackEvent } = useUserAnalytics()

  useEffect(() => {
    if (!loadingToast || !activeToasts[1]) {
      return
    }
    removeToasts([loadingToast])
  }, [activeToasts, loadingToast, removeToasts])

  const TYPE_OPTIONS: {
    id: string
    label: string
    value: string
    caption?: string
  }[] = [
    {
      id: ReviewPageIds.DownloadsPdfId,
      label: 'Order Summary PDF (.pdf)',
      value: FORMAT_PDF,
    },
    {
      id: ReviewPageIds.DownloadsExcelId,
      label: 'Order Summary Excel (.xls)',
      value: FORMAT_EXCEL,
    },
    {
      id: ReviewPageIds.DownloadsLinesheetId,
      label: `${
        isLinesheetVisible ? 'Export Linesheet to Size' : 'Export Order to Size'
      } (.xls)`,
      value: FORMAT_EXPORT_ORDER_OR_LINESHEET,
    },
  ]

  const TYPE_OPTIONS_PLUS_INVOICE = [
    ...TYPE_OPTIONS,
    {
      id: ReviewPageIds.DownloadsInvoiceId,
      label: 'Order Invoice PDF (.pdf)',
      value: FORMAT_INVOICE_PDF,
      caption: 'Report not available until quantities have been added.',
    },
  ]

  const paymentProvider = getConnectedProvider(
    accountPaymentProviderData?.accountPaymentProviders || [],
    'reach',
  )

  const shouldShowInvoiceOption = isProBrandUser
    ? checkProBrandConditions({
        joorPayUseReach: Boolean(joorPayUseReach),
        paymentProvider: paymentProvider
          ? paymentProvider?.paymentProvider
          : null,
      })
    : checkLiteRetailerConditions({
        paymentProvider: paymentProvider
          ? paymentProvider?.paymentProvider
          : null,
        joorPayUseReach: Boolean(joorPayUseReach),
        showInvoicesAutomatically: !!paymentProvider?.retailersCanSeeInvoices,
        paymentLinkSent: !!hasInvoiceBeenSent,
      })

  const options =
    shouldShowInvoiceOption === 'invoice'
      ? TYPE_OPTIONS_PLUS_INVOICE
      : TYPE_OPTIONS

  const exportOrdersToSizeSelected =
    formik.values.format === FORMAT_EXPORT_ORDER_OR_LINESHEET

  const hasNoAdvancedOptions = [
    FORMAT_EXPORT_ORDER_OR_LINESHEET,
    FORMAT_INVOICE_PDF,
  ].some((option) => option === formik.values.format)

  const disableInvoiceForOrder = (label: string) => {
    return totalUnits === 0 && label === TYPE_OPTIONS_PLUS_INVOICE[3].label
  }

  return (
    <>
      <Drawer.Header>Downloads</Drawer.Header>
      <Drawer.Content>
        <Form onSubmit={formik.handleSubmit}>
          <Types>
            {options.map(({ id, label, value, caption }) => (
              <div key={value}>
                <StyledRadioButton
                  key={id}
                  id={id}
                  className={classNames({
                    checked: formik.values.format === value,
                    disabled: disableInvoiceForOrder(label),
                  })}
                  disabled={!!disableInvoiceForOrder(label)}
                  name="format"
                  label={label}
                  value={value}
                  onChange={(event) => {
                    formik.handleChange(event)
                    formik.setFieldValue('options', {})
                    trackEvent(
                      `Order_Review_Download_Modal_(${label.split(' ')[0]})`,
                    )
                  }}
                />
                {!!caption && !!disableInvoiceForOrder(label) && (
                  <Text
                    fontFamily="alpha"
                    letterSpacing="tight"
                    fontSize="extrasmall"
                    color="primary-500"
                  >
                    {caption}
                  </Text>
                )}
              </div>
            ))}
          </Types>
          <Collapsible title="Advanced Options" disabled={hasNoAdvancedOptions}>
            {!hasNoAdvancedOptions && (
              <AdvancedOptions>
                {ADVANCED_OPTIONS_BY_TYPE[formik.values.format].map(
                  ({ label, value }) => (
                    <StyledCheckbox
                      key={value}
                      id={value}
                      label={label}
                      name={value}
                      size="regular"
                      checked={formik.values.options[value] ?? false}
                      onChange={() => {
                        formik.setFieldValue(
                          `options.${value}`,
                          !formik.values.options[value],
                        )
                      }}
                    />
                  ),
                )}
              </AdvancedOptions>
            )}
          </Collapsible>
        </Form>
      </Drawer.Content>
      <Drawer.Footer>
        <Footer>
          <SecondaryButton
            onClick={() => {
              onClose()
              trackEvent('Order_Review_Download_Modal_(Close)')
            }}
          >
            Cancel
          </SecondaryButton>
          <Button
            id={ReviewPageIds.DownloadsButtonId}
            disabled={!!loadingToast}
            type="submit"
            onClick={() => {
              formik.handleSubmit()
              trackEvent('Order_Review_Download_Modal_(Download/Submit)', {
                Checks: getCheckedOptionsText(formik.values).join(', '),
              })
            }}
          >
            Download
          </Button>
        </Footer>
      </Drawer.Footer>
    </>
  )
}
